import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { toast } from 'react-toastify';
import './BecomeArtistPage.css';

const BecomeArtistPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [instagramHandle, setInstagramHandle] = useState('');
    const [portfolioUrl, setPortfolioUrl] = useState('');
    const [userMessage, setUserMessage] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [pendingInviteCode, setPendingInviteCode] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(true);
    const [statusData, setStatusData] = useState(null);

    const checkStatus = React.useCallback(async () => {
        try {
            const response = await api.get('/verification/status');
            setStatusData(response.data);
        } catch (error) {
            console.error('Error checking status:', error);
        } finally {
            setCheckingStatus(false);
        }
    }, []);

    useEffect(() => {
        checkStatus();
    }, [checkStatus]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await api.post('/verification/request', {
                instagramHandle: instagramHandle.startsWith('@') ? instagramHandle : `@${instagramHandle}`,
                portfolioUrl,
                userMessage,
                inviteCode,
                acceptedTerms: true
            });

            toast.success(t('application_sent_success', 'Solicitação enviada com sucesso!'));
            navigate('/upgrade/success');
        } catch (error) {
            console.error(error);
            const msg = error.response?.data?.message || t('application_error', 'Erro ao enviar solicitação.');
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    if (checkingStatus) {
        return (
            <div className="become-artist-container">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    // BLOCKING STATES
    if (statusData?.status === 'PENDING') {
        return (
            <div className="become-artist-container">
                <div className="become-artist-card text-center">
                    <div className="status-icon pending">⏳</div>
                    <h1>{t('pending_request_title', 'Solicitação em Análise')}</h1>
                    <p>{t('pending_request_desc', 'Você já possui uma solicitação pendente. Aguarde a análise da nossa equipe.')}</p>

                    <div className="invite-redeem-section" style={{ margin: '20px 0', padding: '15px', background: 'rgba(100, 108, 255, 0.1)', borderRadius: '8px' }}>
                        <p style={{ marginBottom: '10px', fontSize: '0.9rem' }}>{t('have_invite_code', 'Possui um código de convite?')}</p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <input
                                type="text"
                                placeholder={t('code', 'Código')}
                                value={pendingInviteCode}
                                onChange={e => setPendingInviteCode(e.target.value.toUpperCase())}
                                style={{ background: '#111', border: '1px solid #333', color: 'white', padding: '8px', borderRadius: '4px', width: '120px', textAlign: 'center' }}
                            />
                            <button
                                onClick={async () => {
                                    try {
                                        setLoading(true);
                                        await api.post('/verification/redeem', { inviteCode: pendingInviteCode });
                                        toast.success(t('invite_validated_priority', 'Convite validado! Seu pedido recebeu prioridade.'));
                                        window.location.reload();
                                    } catch (e) {
                                        toast.error(e.response?.data?.message || t('invalid_code', 'Código inválido'));
                                    } finally {
                                        setLoading(false);
                                    }
                                }}
                                disabled={!pendingInviteCode}
                                style={{ background: '#646cff', color: 'white', border: 'none', padding: '0 15px', borderRadius: '4px', cursor: 'pointer' }}
                            >
                                Validar
                            </button>
                        </div>
                    </div>

                    <button onClick={() => navigate('/home')} className="back-btn-primary">
                        {t('back_to_home', 'Voltar para Início')}
                    </button>
                </div>
            </div>
        );
    }

    if (statusData?.status === 'REJECTED' && statusData?.cooldown) {
        return (
            <div className="become-artist-container">
                <div className="become-artist-card text-center">
                    <div className="status-icon rejected">🛑</div>
                    <h1>{t('request_rejected', 'Solicitação Recusada')}</h1>
                    <p>
                        {t('request_rejected_cooldown', 'Sua solicitação anterior foi recusada. Você poderá tentar novamente em {{days}} dias.', { days: statusData.remainingDays })}
                    </p>
                    <button onClick={() => navigate('/home')} className="back-btn-primary">
                        {t('back_to_home', 'Voltar para Início')}
                    </button>
                </div>
            </div>
        );
    }

    if (statusData?.status === 'APPROVED') {
        return (
            <div className="become-artist-container">
                <div className="become-artist-card text-center">
                    <div className="status-icon success">✅</div>
                    <h1>{t('already_artist', 'Você já é um Artista!')}</h1>
                    <button onClick={() => navigate('/home')} className="back-btn-primary">
                        {t('back_to_home', 'Voltar para Início')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="become-artist-container">
            <div className="become-artist-card">
                <div className="form-header">
                    <h1>🚀 {t('become_artist_title', 'Torne-se um Artista')}</h1>
                    <p>{t('become_artist_desc', 'Envie seus dados para verificação e comece a divulgar sua arte.')}</p>
                </div>

                <form onSubmit={handleSubmit} className="become-artist-form">
                    <div className="input-group">
                        <label>{t('instagram_handle', 'Instagram (@seu_usuario)')} *</label>
                        <input
                            type="text"
                            value={instagramHandle}
                            onChange={(e) => setInstagramHandle(e.target.value)}
                            placeholder="@novaink.app"
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label>{t('portfolio_url', 'Link do Portfólio (Opcional)')}</label>
                        <input
                            type="url"
                            value={portfolioUrl}
                            onChange={(e) => setPortfolioUrl(e.target.value)}
                            placeholder="https://behance.net/seu_perfil"
                        />
                    </div>

                    <div className="input-group">
                        <label>{t('message_to_admin', 'Mensagem (O conteúdo da sua solicitação)')}</label>
                        <textarea
                            value={userMessage}
                            onChange={(e) => setUserMessage(e.target.value)}
                            placeholder={t('message_to_admin_placeholder', 'Conte-nos um pouco sobre sua experiência...')}
                            rows="4"
                        ></textarea>
                    </div>

                    <div className="input-group">
                        <label>{t('invite_code_optional', 'Código de Convite (Opcional)')}</label>
                        <input
                            type="text"
                            value={inviteCode}
                            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                            placeholder={t('invite_code_placeholder', 'Se tiver um código, digite aqui')}
                        />
                        <span className="field-hint">{t('invite_code_hint', 'Códigos de convite dão prioridade na análise da sua solicitação.')}</span>
                    </div>

                    <div className="input-group">
                        <label className="custom-checkbox" htmlFor="artistConsent">
                            <input
                                type="checkbox"
                                id="artistConsent"
                                checked={acceptedTerms}
                                onChange={(e) => setAcceptedTerms(e.target.checked)}
                                required
                            />
                            <span className="checkmark"></span>
                            <span className="checkbox-text">
                                {t("accept_artist_terms", "Eu li e aceito os")}{" "}
                                <Link to="/terms" target="_blank" onClick={(e) => e.stopPropagation()}>
                                    {t('terms_of_service_title', 'Termos de Uso')}
                                </Link>{" "}
                                {t('and', 'e')}{" "}
                                <Link to="/privacy" target="_blank" onClick={(e) => e.stopPropagation()}>
                                    {t('privacy_policy_title', 'Política de Privacidade')}
                                </Link>{" "}
                                {t("artist_specific_notice", "específicos para artistas.")}
                            </span>
                        </label>
                    </div>

                    <div className="form-actions">
                        <button type="button" onClick={() => navigate('/home')} className="cancel-btn">
                            {t('cancel', 'Cancelar')}
                        </button>
                        <button type="submit" className="submit-btn" disabled={loading}>
                            {loading ? t('sending', 'Enviando...') : t('submit_application', 'Enviar Solicitação')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BecomeArtistPage;
